import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const mobilePanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpen(false);
      }
      if (
        mobileMenuOpen &&
        mobilePanelRef.current &&
        !mobilePanelRef.current.contains(target) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(target)
      ) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleHamburgerClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setMobileMenuOpen((prev) => !prev);
    },
    [],
  );

  const navLinkClass = (scrolled: boolean) =>
    `text-sm font-semibold whitespace-nowrap transition-all duration-300 cursor-pointer ${
      scrolled
        ? 'text-foreground-700 hover:text-primary-600'
        : 'text-white/95 hover:text-white drop-shadow-sm'
    }`;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
            : 'bg-black/15 backdrop-blur-[2px]'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link
            to="/"
            className={`text-lg md:text-xl font-bold font-heading whitespace-nowrap transition-all duration-300 ${
              isScrolled ? 'text-foreground-950' : 'text-white drop-shadow-md'
            }`}
          >
            USFansSpreadsheet
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className={navLinkClass(isScrolled)}>
              Strona główna
            </Link>

            <Link
              to="/artykul/usfansspreadsheet-kompletny-przewodnik"
              className={navLinkClass(isScrolled)}
            >
              Przewodnik
            </Link>

            <Link
              to="/artykul/usfansspreadsheet-szablony"
              className={navLinkClass(isScrolled)}
            >
              Przewodnik zakupowy
            </Link>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={navLinkClass(isScrolled)}
            >
              Kaspreadsheet
            </a>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold whitespace-nowrap transition-all duration-300 cursor-pointer ${
                  isScrolled
                    ? 'border-foreground-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600'
                    : 'border-white/60 text-white hover:border-white hover:text-white'
                }`}
              >
                Kategorie
                <i
                  className={`ri-arrow-down-s-line transition-transform duration-300 ${
                    isOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>

              {isOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-background-50 rounded-xl border border-background-200 shadow-lg py-3 max-h-[460px] overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 cursor-pointer whitespace-nowrap"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Hamburger */}
          <button
            ref={hamburgerRef}
            onClick={handleHamburgerClick}
            aria-label={mobileMenuOpen ? 'Zamknij menu' : 'Otwórz menu'}
            className={`md:hidden flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-300 cursor-pointer ${
              isScrolled
                ? 'text-foreground-700 hover:bg-background-200'
                : 'text-white hover:bg-white/10'
            }`}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </nav>

      {/* Mobile Menu — rendered at body level via portal to avoid stacking context issues */}
      {mobileMenuOpen &&
        createPortal(
          <div className="md:hidden fixed inset-0 z-[9999] animate-fade-in">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={closeMobileMenu}
            ></div>

            {/* Panel */}
            <div
              ref={mobilePanelRef}
              className="absolute top-0 right-0 bottom-0 w-[85vw] max-w-[400px] bg-background-50 overflow-y-auto shadow-2xl animate-slide-in-right"
            >
              {/* Close button inside panel */}
              <div className="sticky top-0 z-10 bg-background-50 border-b border-background-200 flex items-center justify-between px-4 h-16">
                <span className="text-sm font-semibold text-foreground-400 font-label uppercase tracking-wider">
                  Menu
                </span>
                <button
                  onClick={closeMobileMenu}
                  className="flex items-center justify-center w-9 h-9 rounded-lg text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
                  aria-label="Zamknij menu"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <div className="px-4 py-5 space-y-6">
                {/* Navigation links */}
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 font-label px-1">
                    Nawigacja
                  </p>
                  <Link
                    to="/"
                    onClick={closeMobileMenu}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-home-line text-primary-500 text-lg"></i>
                    Strona główna
                  </Link>
                  <Link
                    to="/artykul/usfansspreadsheet-kompletny-przewodnik"
                    onClick={closeMobileMenu}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-book-open-line text-primary-500 text-lg"></i>
                    Przewodnik
                  </Link>
                  <Link
                    to="/artykul/usfansspreadsheet-szablony"
                    onClick={closeMobileMenu}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-shopping-bag-line text-primary-500 text-lg"></i>
                    Przewodnik zakupowy
                  </Link>
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeMobileMenu}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-external-link-line text-accent-500 text-lg"></i>
                    Kaspreadsheet
                  </a>
                </div>

                {/* Categories */}
                <div className="border-t border-background-200 pt-6">
                  <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 font-label px-1">
                    Kategorie ({categories.length})
                  </p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={closeMobileMenu}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>

                {/* CTA buttons */}
                <div className="border-t border-background-200 pt-6 pb-4">
                  <div className="flex flex-col gap-2.5">
                    <a
                      href="https://kaspreadsheet.com/"
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={closeMobileMenu}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      usfans spreadsheet
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://kaspreadsheet.com/"
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={closeMobileMenu}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-accent-500 text-white font-semibold text-sm hover:bg-accent-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      buy it now
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}